import React, { useState } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { Button } from './Button';
import { TextField } from './forms/TextField';
import { SelectField } from './forms/SelectField';
import { TextAreaField } from './forms/TextAreaField';
import { CheckboxField } from './forms/CheckboxField';
import { SweepLoader } from './SweepLoader';
import { Dial } from './Dial';
import { readings } from '../data/readings';
import { isValidEmail, isValidPhone } from '../utils/validation';
import type { ReadingId } from '../types/content';
const FOCUS_AREAS = ['Love', 'Career', 'Finances', 'Purpose', 'Family', 'Other'];
type BookingFormProps = {
  readingType: ReadingId | '';
  onReadingTypeChange: (id: ReadingId | '') => void;
};

/**
 * Booking / intake form. UI only — field names map to the 1Dash CRM:
 * name, email, mobile, reading_type, birth_date, birth_time, birth_time_unknown, birth_city,
 * focus_area, focus_notes, partner_name, partner_birth_date, partner_birth_time,
 * partner_birth_time_unknown, partner_birth_city, sms_consent.
 */
export function BookingForm({
  readingType,
  onReadingTypeChange
}: BookingFormProps) {
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success'>('idle');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [timeUnknown, setTimeUnknown] = useState(false);
  const [partnerTimeUnknown, setPartnerTimeUnknown] = useState(false);
  const [focusArea, setFocusArea] = useState('');
  const [firstName, setFirstName] = useState('');
  const reduce = useReducedMotion();
  const isSynastry = readingType === 'synastry';
  const selected = readings.find(r => r.id === readingType);
  function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const d = new FormData(e.currentTarget);
    const get = (k: string) => String(d.get(k) ?? '').trim();
    const next: Record<string, string> = {};
    if (!get('name')) next.name = 'Enter your full name.';
    if (!isValidEmail(get('email'))) next.email = 'Enter a valid email address.';
    if (get('mobile') && !isValidPhone(get('mobile'))) next.mobile = 'Enter a valid mobile number.';
    if (d.get('sms_consent') === 'on' && !get('mobile')) next.mobile = 'Add your mobile number to receive texts.';
    if (!readingType) next.reading_type = 'Choose a reading.';
    if (!get('birth_date')) next.birth_date = 'Enter your birth date.';
    if (!timeUnknown && !get('birth_time')) next.birth_time = 'Enter your birth time, or tick "I don\'t know my exact time".';
    if (!get('birth_city')) next.birth_city = 'Enter your birth city and country.';
    if (isSynastry) {
      if (!get('partner_name')) next.partner_name = "Enter your partner's name.";
      if (!get('partner_birth_date')) next.partner_birth_date = "Enter your partner's birth date.";
      if (!partnerTimeUnknown && !get('partner_birth_time')) next.partner_birth_time = 'Enter their birth time, or tick the box below.';
      if (!get('partner_birth_city')) next.partner_birth_city = "Enter your partner's birth city.";
    }
    setErrors(next);
    const first = Object.keys(next)[0];
    if (first) {
      document.getElementById(`f-${first}`)?.focus();
      return;
    }
    setFirstName(get('name').split(' ')[0]);
    setStatus('submitting');
    window.setTimeout(() => setStatus('success'), 1100);
  }
  if (status === 'success') {
    return <div role="status" className="bg-cream p-[clamp(1.75rem,4vw,4.5rem)]" data-id="element-32" data-mp="33yjbdue8ng92">
        <Dial numerals="twelve" star={false} className="h-24 w-24" data-id="element-33" data-mp="2z1j6vnx1elk2" />
        <h3 className="t-h2 mt-8" data-id="element-34" data-mp="3dy0fw8hr7o3g">Thank you, {firstName}.</h3>
        <p className="t-lead mt-4 max-w-[48ch] text-graphite" data-id="element-35" data-mp="3dxie6wcboho5">
          Your request for a {selected?.name ?? 'reading'} is in. We'll email you to confirm your session time and payment.
        </p>
        <ol className="mt-10 grid gap-6 border-t border-stone pt-8 md:grid-cols-3" data-id="element-36" data-mp="1jkj057nlbviz">
          {[['I', 'Confirm your time', 'You will receive an email to choose your session time.'], ['II', 'Abby studies your chart', 'About an hour of preparation before you meet.'], ['III', 'Meet by phone or video', 'One hour together, focused on what matters to you now.']].map(([n, t, b]) => <li key={n} data-id="element-37" data-mp="fkaw46jerdbk">
              <span className="t-numeral text-gold-deep" data-id="element-38" data-mp="jo8yqr7xz69d">{n}</span>
              <p className="mt-2 text-charcoal" data-id="element-39" data-mp="jledixh3thew">{t}</p>
              <p className="mt-1 text-sm text-graphite" data-id="element-40" data-mp="3j45lwr4xuup1">{b}</p>
            </li>)}
        </ol>
      </div>;
  }
  const legend = (numeral: string, title: string, sub?: string) => <legend className="mb-6 w-full" data-id="element-41" data-mp="3s2hjxe84wuri">
      <span className="flex items-baseline gap-4" data-id="element-42" data-mp="3s1fwsbdughtf">
        <span className="t-numeral text-lg text-gold-deep" data-id="element-43" data-mp="p4q3jkbbei8">{numeral}</span>
        <span className="t-h3" data-id="element-44" data-mp="30cmjalettinj">{title}</span>
      </span>
      {sub && <span className="mt-2 block pl-9 text-sm text-graphite" data-id="element-45" data-mp="30c4hzadb4e8r">{sub}</span>}
    </legend>;
  return <form onSubmit={handleSubmit} noValidate aria-label="Book a reading" className="space-y-[clamp(2.75rem,4vw,4rem)]" data-id="element-46" data-mp="34bvdfrds3ijm">
      <fieldset data-id="element-47" data-mp="2rvcgko1g1i9d">
        {legend('I', 'About you')}
        <div className="grid gap-5 md:grid-cols-2" data-id="element-48" data-mp="2rwnhmg3i8bws">
          <TextField label="Full name" name="name" autoComplete="name" required error={errors.name} className="md:col-span-2" data-id="element-49" data-mp="2ntij0omjoudf" />
          <TextField label="Email" name="email" type="email" autoComplete="email" required error={errors.email} data-id="element-50" data-mp="2itmfeqhrf4ye" />
          <TextField label="Mobile" name="mobile" type="tel" autoComplete="tel" optional error={errors.mobile} data-id="element-51" data-mp="2dwcspilr97e9" />
          <SelectField label="Reading type" name="reading_type" className="md:col-span-2" placeholder="Choose a reading" value={readingType} onChange={e => onReadingTypeChange(e.target.value as ReadingId)} error={errors.reading_type} options={readings.map(r => ({
          value: r.id,
          label: `${r.name}: ${r.tagline} · $${r.price}`
        }))} data-id="element-52" data-mp="293jnyuqadz26" />
        </div>
      </fieldset>

      <fieldset data-id="element-53" data-mp="1ghcwk4nfzsi7">
        {legend('II', 'Your birth details', 'Your exact time and place make the chart yours.')}
        <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-3" data-id="element-54" data-mp="1li92sov9nqqs">
          <TextField label="Birth date" name="birth_date" type="date" required error={errors.birth_date} data-id="element-55" data-mp="16kqbber7ezfn" />
          <TextField label="Exact birth time" name="birth_time" type="time" disabled={timeUnknown} error={errors.birth_time} data-id="element-56" data-mp="1bii5ygj8gznj" />
          <TextField label="Birth city & country" name="birth_city" placeholder="e.g. Las Vegas, USA" autoComplete="off" required error={errors.birth_city} className="md:col-span-2 xl:col-span-1" data-id="element-57" data-mp="1z9e55yq0qx4t" />
        </div>
        <CheckboxField name="birth_time_unknown" className="mt-5" checked={timeUnknown} onChange={e => setTimeUnknown(e.target.checked)} data-id="element-58" data-mp="1pisx7koggg98">
          I don't know my exact time
        </CheckboxField>
        <p className="mt-3 max-w-[64ch] border-l border-gold pl-4 text-sm text-graphite" data-id="element-59" data-mp="2i6fbbmvb73ac">
          Why it matters: your birth time sets your rising sign and the twelve houses. Even a few minutes can shift them. Check your
          birth certificate if you can; Abby can still read your chart without it, with less precision on timing.
        </p>
      </fieldset>

      <AnimatePresence initial={false} data-id="element-60" data-mp="l89d21m04jcl">
        {isSynastry && <motion.fieldset key="partner" initial={reduce ? {
        opacity: 0
      } : {
        opacity: 0,
        y: 8
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.25,
        ease: [0.23, 1, 0.32, 1]
      }} data-id="element-61" data-mp="gag799om1rcu">
            {legend('III', "Your partner's details", 'Synastry overlays two charts, so we need both.')}
            <div className="grid gap-5 md:grid-cols-2" data-id="element-62" data-mp="3tp448jtfsfau">
              <TextField label="Partner's name" name="partner_name" error={errors.partner_name} className="md:col-span-2" data-id="element-63" data-mp="3tob2xyd8yi03" />
              <TextField label="Partner's birth date" name="partner_birth_date" type="date" error={errors.partner_birth_date} data-id="element-64" data-mp="1d2jsobkn0z7u" />
              <TextField label="Partner's birth time" name="partner_birth_time" type="time" disabled={partnerTimeUnknown} error={errors.partner_birth_time} data-id="element-65" data-mp="32hugcanc3jyj" />
              <TextField label="Partner's birth city & country" name="partner_birth_city" error={errors.partner_birth_city} className="md:col-span-2" data-id="element-66" data-mp="37gypj1a9dhov" />
            </div>
            <CheckboxField name="partner_birth_time_unknown" className="mt-5" checked={partnerTimeUnknown} onChange={e => setPartnerTimeUnknown(e.target.checked)} data-id="element-67" data-mp="3h7jwpd3zzuje">
              I don't know their exact time
            </CheckboxField>
          </motion.fieldset>}
      </AnimatePresence>

      <fieldset data-id="element-68" data-mp="3v7b610jkkmr2">
        {legend(isSynastry ? 'IV' : 'III', 'Your focus', 'What would you most like to understand?')}
        <div role="radiogroup" aria-label="Focus area" className="flex flex-wrap gap-2" data-id="element-69" data-mp="3zgi2uyeecoz">
          {FOCUS_AREAS.map(f => <label key={f} className="cursor-pointer" data-id="element-70" data-mp="20ubhghx7oi7n">
              <input type="radio" name="focus_area" value={f} checked={focusArea === f} onChange={() => setFocusArea(f)} className="peer sr-only" data-id="element-71" data-mp="25sd1p66dinkp" />
              <span className="t-label inline-flex h-11 items-center border border-[#8F877F] px-5 text-[0.66rem] text-charcoal transition-colors duration-150 hover:border-charcoal peer-checked:border-charcoal peer-checked:bg-charcoal peer-checked:text-white peer-focus-visible:outline peer-focus-visible:outline-2 peer-focus-visible:outline-offset-2 peer-focus-visible:outline-charcoal" data-id="element-72" data-mp="my1wktx969iv">
                {f}
              </span>
            </label>)}
        </div>
        <TextAreaField label="Tell Abby more" name="focus_notes" optional className="mt-6" placeholder="Questions, decisions or chapters of life you'd like to explore." data-id="element-73" data-mp="11tzpm3bvqxqv" />
      </fieldset>

      <div className="border-t border-stone pt-8" data-id="element-74" data-mp="ruil3p8d6pwh">
        <CheckboxField name="sms_consent" data-id="element-75" data-mp="3hkcehmqf0wcr">
          Send me appointment reminders and updates by text. Msg &amp; data rates may apply; reply STOP to opt out.
        </CheckboxField>
        <p className="mt-3 pl-7 text-sm text-graphite" data-id="element-76" data-mp="3cnvoso3ki9xk">
          By submitting, you agree to receive emails about your booking from True Twelve. You won't be charged yet.
        </p>
        <div className="mt-8 flex flex-col gap-4 sm:flex-row sm:items-center" data-id="element-77" data-mp="24m1dx4wany80">
          <Button type="submit" size="lg" disabled={status === 'submitting'} className="w-full sm:w-auto" data-id="element-78" data-mp="2jeldxwo5dpl8">
            {status === 'submitting' ? <>
                <SweepLoader label="Sending request" data-id="element-79" data-mp="2scxeoriqa3re" /> Sending
              </> : 'Request My Reading'}
          </Button>
          {selected && <p className="text-sm text-graphite" data-id="element-80" data-mp="32cfwb1b61dga">
              {selected.name} · {selected.duration} · ${selected.price}
            </p>}
        </div>
      </div>
    </form>;
}